import { QdsNativeField } from '../shared/native-field.js';
import type { FieldValidity } from '../shared/native-field.js';
import { isAttrTruthy, reflectBoolean } from '../shared/form-control.js';
import { AnchoredOverlayController } from '../shared/overlay-utils.js';
import { createListboxOption, renderGroupedOptions } from '../shared/option-list.js';
import { normalizeChoiceOptions } from '../shared/option-model.js';
import type { QdsOption } from '../../types/index';

export type QdsSuggestionProvider = (query: string, context: { signal: AbortSignal }) => readonly QdsOption[] | Promise<readonly QdsOption[]>;

/**
 * Editable known-identity selection with local or cancellable async suggestions.
 * @element qds-combobox
 * @attr {string} label - Visible input label.
 * @attr {string} aria-label - Explicit accessible name.
 * @attr {string} aria-labelledby - External label IDs.
 * @attr {string} name - Native submission key.
 * @attr {string} form - Associated native form ID.
 * @attr {string} value - Reset/default identity, not the current draft.
 * @attr {string} placeholder - Editing hint; not a substitute for a label.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Override validation message.
 * @attr {boolean} required - Require a committed identity or allowed custom text.
 * @attr {boolean} disabled - Disable interaction and omit submission.
 * @attr {boolean} readonly - Preserve value and prevent requests or editing.
 * @attr {boolean} invalid - Author-forced invalid state.
 * @attr {boolean} allow-custom - Allow arbitrary text as the submitted string; default false.
 * @attr {boolean} clearable - Show a named clear action.
 * @attr {number} min-chars - Minimum typed characters for suggestions; default 0.
 * @attr {number} debounce - Provider/local filtering delay in milliseconds; default 150.
 * @attr {'sm'|'md'|'lg'} size - Input size; touch actions remain at least 44px.
 * @attr {'blur'|'input'|'submit'} validate-on - Validation display timing; default blur.
 * @property {string} value - Canonical selected identity or allowed custom string; silent assignment.
 * @property {string} defaultValue - Native reset default.
 * @property {ReadonlyArray<{value:string;label:string;disabled?:boolean;group?:string;description?:string}>} options - Local options or initial metadata for a remote identity.
 * @property {((query:string,context:{signal:AbortSignal})=>ReadonlyArray<{value:string;label:string;disabled?:boolean;group?:string;description?:string}>|Promise<ReadonlyArray<{value:string;label:string;disabled?:boolean;group?:string;description?:string}>>)|null} suggestionProvider - Application-owned async source; null uses local options.
 * @property {string} query - Readonly displayed draft, distinct from the submitted identity.
 * @property {{value:string;label:string;disabled?:boolean;group?:string;description?:string}|null} selectedOption - Readonly cloned committed metadata, retained across remote searches.
 * @property {boolean} open - Readonly popup state; use show/hide.
 * @property {'idle'|'loading'|'ready'|'empty'|'error'|'minimum'} status - Readonly suggestion request state.
 * @csspart input - Editable semantic combobox and primary focus target.
 * @csspart label - Visible label.
 * @csspart toggle - Open/close suggestions action.
 * @csspart clear-button - Optional clear action.
 * @csspart popup - Same-root top-layer suggestion popup.
 * @csspart options - Named suggestion listbox.
 * @csspart option - Plain-text suggestion with noninteractive description.
 * @csspart group - Named contiguous group.
 * @csspart group-label - Visible group name.
 * @csspart status - Live loading/error/empty feedback.
 * @csspart retry - Retry failed source action.
 * @csspart hint - Helper text.
 * @csspart error - Validation feedback.
 * @fires {{component:HTMLElement;value:string;query:string;option:{value:string;label:string;disabled?:boolean;group?:string;description?:string}|null}} input - User draft/identity change; native events are contained.
 * @fires {{component:HTMLElement;value:string;query:string;option:{value:string;label:string;disabled?:boolean;group?:string;description?:string}|null}} change - Explicit selection or committed editing change, emitted once.
 * @fires {{component:HTMLElement;open:boolean;reason:string}} open-change - Popup visibility changed.
 * @fires {{component:HTMLElement;query:string;error:unknown}} suggestions-error - Current provider failed; stale/aborted failures are ignored.
 * @fires {{component:HTMLElement;valid:boolean;validity:{valid:boolean;valueMissing:boolean;badInput?:boolean;customError:boolean};message:string}} qds-validity-change - Validation transition.
 */
export class QdsCombobox extends QdsNativeField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'value', 'placeholder', 'allow-custom', 'clearable', 'min-chars', 'debounce'];
  }
  private _value = '';
  private _query = '';
  private _options: QdsOption[] = [];
  private _selected: QdsOption | null = null;
  private _custom = false;
  private _editing = false;
  private _provider: QdsSuggestionProvider | null = null;
  private _results: QdsOption[] = [];
  private _active = '';
  private _status: 'idle' | 'loading' | 'ready' | 'empty' | 'error' | 'minimum' = 'idle';
  private _overlay?: AnchoredOverlayController;
  private _panel?: HTMLElement;
  private _list?: HTMLElement;
  private _toggle?: HTMLButtonElement;
  private _clear?: HTMLButtonElement;
  private _retry?: HTMLButtonElement;
  private _statusNode?: HTMLElement;
  private _abort?: AbortController;
  private _timer?: ReturnType<typeof setTimeout>;
  private _generation = 0;
  private _direction = 0;
  private _composing = false;
  private _compositionResult: string | undefined;
  private _committed = '';

  get value(): string { return this._value; }
  set value(value: string) {
    this._dirty = true; this.hide(); this._assign(String(value ?? '')); this._sync(); this._committed = this._signature();
  }
  get defaultValue(): string { return this.getAttribute('value') || ''; }
  set defaultValue(value: string) { this.setAttribute('value', value ?? ''); }
  get query(): string { return this._query; }
  get selectedOption(): QdsOption | null { return this._selected ? { ...this._selected } : null; }
  get open(): boolean { return this._overlay?.open || false; }
  get status(): 'idle' | 'loading' | 'ready' | 'empty' | 'error' | 'minimum' { return this._status; }
  get options(): QdsOption[] { return this._options.map(option => ({ ...option })); }
  set options(value: readonly QdsOption[]) {
    this._options = normalizeChoiceOptions(value);
    const known = this._options.find(option => option.value === this.value);
    if (known) { this._selected = { ...known }; this._custom = false; if (!this._editing) { this._query = known.label; this._committed = this._signature(); } }
    this._sync(); if (this.open && !this._provider) this._request(false);
  }
  get suggestionProvider(): QdsSuggestionProvider | null { return this._provider; }
  set suggestionProvider(value: QdsSuggestionProvider | null) { this.hide(); this._provider = typeof value === 'function' ? value : null; }
  get placeholder(): string { return this.getAttribute('placeholder') || ''; }
  set placeholder(value: string) { this.setAttribute('placeholder', value ?? ''); }
  get allowCustom(): boolean { return isAttrTruthy(this.getAttribute('allow-custom')); }
  set allowCustom(value: boolean) { reflectBoolean(this, 'allow-custom', value); }
  get clearable(): boolean { return isAttrTruthy(this.getAttribute('clearable')); }
  set clearable(value: boolean) { reflectBoolean(this, 'clearable', value); }
  get minChars(): number { return this._number('min-chars', 0, 10000); }
  set minChars(value: number) { this.setAttribute('min-chars', String(value)); }
  get debounce(): number { return this._number('debounce', 150, 60000); }
  set debounce(value: number) { this.setAttribute('debounce', String(value)); }
  protected override get _upgradeNames(): string[] { return ['options', 'value', 'defaultValue', 'suggestionProvider', 'placeholder', 'allowCustom', 'clearable', 'minChars', 'debounce']; }
  private _number(name: string, fallback: number, maximum: number): number {
    const raw = this.getAttribute(name); if (raw === null || !raw.trim()) return fallback;
    const value = Number(raw); return Number.isFinite(value) && value >= 0 ? Math.min(maximum, Math.floor(value)) : fallback;
  }
  override attributeChangedCallback(name = '', oldValue: string | null = null, newValue: string | null = null): void {
    if (name === 'value' && !this._dirty) { this._assign(this.defaultValue); this._committed = this._signature(); }
    super.attributeChangedCallback();
    if (oldValue !== newValue && this.open && ['min-chars', 'debounce'].includes(name)) this._request(false);
  }
  override focus(options?: FocusOptions): void { if (!this.disabled) super.focus(options); }
  /** Open suggestions without selecting a result. Committed labels request all options. */
  show(): void { if (this._present('api')) { this.focus(); this._request(true); } }
  /** Close and abort pending requests; preserve draft and committed identity. */
  hide(): void { this._close('api'); }
  /** Retry the current query immediately after an application provider fails. */
  retry(): void { if (this._present('retry')) { this.focus(); this._request(true); } }
  /** User-style clear: emit input/change, restore input focus, and cancel requests. */
  clear(): void {
    if (this.disabled || this.readonly) return;
    const changed = Boolean(this.value || this.query); this.hide(); this._dirty = true; this._assign(''); this._sync(); this.focus();
    if (changed) { this._emit('input', this._detail()); this._commit(); }
  }
  private _assign(value: string): void {
    this._composing = false; this._compositionResult = undefined;
    this._value = value; this._selected = this._options.find(option => option.value === value) || null;
    this._query = this._selected?.label || value; this._custom = false; this._editing = false;
  }
  protected _render(): void {
    if (!this._dirty) this._assign(this.defaultValue);
    this._committed = this._signature();
    this.shadowRoot!.innerHTML = `<style>${this._styles}
      :host { display:block; width:100%; } * { box-sizing:border-box; }
      .input-row { display:flex; align-items:center; gap:.25rem; }
      input.qds-control-surface { flex:1; width:100%; min-width:0; min-height:44px; padding:.625rem .75rem; }
      button { font:inherit; color:var(--theme-text-primary,#333); background:var(--theme-background-primary,white);
        border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:4px; min-width:44px; min-height:44px; cursor:pointer; }
      button:focus-visible { outline:2px solid var(--theme-accent-primary,#445e89); outline-offset:2px; }
      button:disabled { opacity:.55; cursor:not-allowed; }
      [part=popup] { position:fixed; margin:0; padding:.25rem; overflow:auto; width:var(--qds-combobox-popup-width,24rem);
        color:var(--theme-text-primary,#333); background:var(--theme-background-primary,white); border:1px solid var(--theme-border-secondary,#a0aabe);
        border-radius:4px; box-shadow:var(--theme-elevation-2,0 4px 12px #0003); max-height:20rem; }
      [part=option] { display:flex; flex-direction:column; align-items:flex-start; justify-content:center; width:100%; border:0;
        text-align:start; overflow-wrap:anywhere; padding:.625rem .75rem; border-radius:0; }
      [part=option][aria-selected=true] { color:var(--theme-text-on-accent-primary,white); background:var(--theme-accent-primary,#445e89); }
      .option-description { font-size:.875em; } [part=group-label] { padding:.5rem .75rem; font-weight:600; background:var(--theme-background-secondary,#f9fafb); }
      [part=status] { margin:.5rem; } [part=retry] { margin:.25rem; padding:.5rem .75rem; }
      @media(forced-colors:active) { [part=option][aria-selected=true] { color:HighlightText; background:Highlight; } }
    </style><label class="qds-label" part="label" for="${this._id}"><span class="label-text"></span><span class="qds-required-marker" aria-hidden="true">*</span></label>
    <div class="input-row"><input id="${this._id}" part="input" class="qds-control-surface" type="text" role="combobox" aria-autocomplete="list" aria-haspopup="listbox" autocomplete="off">
    <button part="clear-button" type="button" aria-label="Clear selection">×</button><button part="toggle" type="button" tabindex="-1" aria-label="Show suggestions">▾</button></div>
    <div class="qds-feedback"><div class="qds-hint" part="hint" id="${this._id}-hint"></div><div class="qds-error" part="error" id="${this._id}-error" aria-live="polite"></div></div>
    <div part="popup"><div part="options" id="${this._id}-options" role="listbox"></div><p part="status" role="status" aria-live="polite"></p><button part="retry" type="button">Retry suggestions</button></div>`;
    this._control = this.shadowRoot!.querySelector('input')!;
    this._panel = this.shadowRoot!.querySelector('[part=popup]')!;
    this._list = this.shadowRoot!.querySelector('[part=options]')!;
    this._toggle = this.shadowRoot!.querySelector('[part=toggle]')!;
    this._clear = this.shadowRoot!.querySelector('[part=clear-button]')!;
    this._retry = this.shadowRoot!.querySelector('[part=retry]')!;
    this._statusNode = this.shadowRoot!.querySelector('[part=status]')!;
    this._overlay = new AnchoredOverlayController(this, this._panel, {
      anchor: () => this._control || null, escape: () => !this._composing,
      onDismiss: reason => { this._close(reason); if (reason === 'escape') this.focus(); },
    });
  }
  protected _syncControl(): void {
    if (this.disabled || this.readonly) this._close('unavailable');
    if (this._control!.value !== this.query) this._control!.value = this.query;
    (this._control as HTMLInputElement).placeholder = this.placeholder;
    this._toggle!.disabled = this.disabled || this.readonly; this._clear!.disabled = this.disabled || this.readonly;
    this._clear!.hidden = !this.clearable || !this.query; this._updatePopup();
  }
  private _present(reason: string): boolean {
    if (this.disabled || this.readonly || !this._overlay) return false;
    if (!this.open && this._overlay.show()) { this._updatePopup(); this._notify('open-change', { open: true, reason }); }
    return this.open;
  }
  private _cancel(): void {
    this._generation++; clearTimeout(this._timer); this._timer = undefined;
    this._abort?.abort(); this._abort = undefined; this._direction = 0;
  }
  private _close(reason: string): void {
    this._cancel(); this._active = ''; this._status = 'idle';
    const wasOpen = this.open; this._overlay?.hide(); this._updatePopup();
    if (wasOpen) this._notify('open-change', { open: false, reason });
  }
  private _request(immediate: boolean, direction = 0): void {
    this._cancel(); this._direction = direction; this._active = ''; this._results = [];
    const query = this._editing ? this.query : '';
    if (query.length < this.minChars && this._editing) { this._status = 'minimum'; this._renderResults(); return; }
    this._status = 'loading'; this._renderResults();
    const generation = this._generation;
    if (immediate || this.debounce === 0) void this._load(query, generation);
    else this._timer = setTimeout(() => { this._timer = undefined; void this._load(query, generation); }, this.debounce);
  }
  private async _load(query: string, generation: number): Promise<void> {
    const controller = new AbortController(); this._abort = controller;
    try {
      const source = this._provider ? await this._provider(query, { signal: controller.signal }) : this._options.filter(option => `${option.label} ${option.description || ''}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
      if (controller.signal.aborted || generation !== this._generation || !this.open) return;
      if (!Array.isArray(source)) throw new TypeError('Suggestion provider must return an option array.');
      this._results = normalizeChoiceOptions(source); this._status = this._results.length ? 'ready' : 'empty';
      const enabled = this._results.filter(option => !option.disabled);
      if (this._direction) this._active = (this._direction > 0 ? enabled[0] : enabled[enabled.length - 1])?.value || '';
      this._direction = 0; this._renderResults();
    } catch (error) {
      if (controller.signal.aborted || generation !== this._generation || !this.open) return;
      this._status = 'error'; this._results = []; this._renderResults(); this._notify('suggestions-error', { query, error });
    } finally { if (this._abort === controller) this._abort = undefined; }
  }
  private _renderResults(): void {
    let index = 0;
    renderGroupedOptions(this._list!, this._results, option => {
      const button = createListboxOption(option); button.tabIndex = -1; button.disabled = Boolean(option.disabled);
      button.setAttribute('role', 'option'); button.setAttribute('aria-disabled', String(button.disabled));
      button.id = `${this._id}-suggestion-${index++}`; return button;
    });
    this._updatePopup(); this._overlay?.reposition();
  }
  private _updatePopup(): void {
    if (!this._control) return;
    this._control.setAttribute('aria-expanded', String(this.open));
    this._control.setAttribute('aria-controls', this._list!.id);
    this._list!.setAttribute('aria-label', this.label || this._control.getAttribute('aria-label') || 'Suggestions');
    this._list!.setAttribute('aria-busy', String(this.status === 'loading'));
    this._toggle!.setAttribute('aria-label', this.open ? 'Hide suggestions' : 'Show suggestions');
    let active: HTMLElement | undefined;
    for (const button of this._list!.querySelectorAll<HTMLElement>('[data-choice]')) {
      const selected = this.open && button.dataset.choice === this._active; button.setAttribute('aria-selected', String(selected));
      if (selected) active = button;
    }
    if (active) this._control.setAttribute('aria-activedescendant', active.id); else this._control.removeAttribute('aria-activedescendant');
    const message = this.status === 'loading' ? 'Loading suggestions…' : this.status === 'error' ? 'Suggestions could not be loaded.' : this.status === 'empty' ? 'No matching suggestions.' : this.status === 'minimum' ? `Type at least ${this.minChars} characters.` : this.status === 'ready' ? `${this._results.length} suggestions available.` : '';
    if (this._statusNode!.textContent !== message) this._statusNode!.textContent = message;
    this._retry!.hidden = this.status !== 'error';
  }
  private _edit(): void {
    this._dirty = true; this._editing = true; this._query = this._control!.value;
    this._value = this.allowCustom ? this.query : ''; this._selected = null; this._custom = Boolean(this.query);
    if (this.validateOn === 'input') this._showError = true;
    this._sync(); this._emit('input', this._detail());
    if (this._present('input')) this._request(false);
  }
  private _detail(): { value: string; query: string; option: QdsOption | null } { return { value: this.value, query: this.query, option: this.selectedOption }; }
  private _signature(): string { return JSON.stringify([this.value, this.query]); }
  private _commit(): void {
    const signature = this._signature(); if (signature === this._committed) return;
    this._committed = signature; this._emit('change', this._detail());
  }
  private _choose(value: string): void {
    const option = this._results.find(candidate => candidate.value === value);
    if (!option || option.disabled || this.disabled || this.readonly) return;
    this._dirty = true; this._value = option.value; this._query = option.label; this._selected = { ...option }; this._custom = false; this._editing = false;
    this._close('selection'); this._sync(); this.focus(); this._emit('input', this._detail()); this._commit();
  }
  private _notify(name: string, detail: object): void { this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail: { component: this, ...detail } })); }
  private _onInput = (event: Event): void => {
    event.stopPropagation();
    if (this.disabled || this.readonly) { this._sync(); return; }
    if (this._composing || (event as InputEvent).isComposing) { this._query = this._control!.value; return; }
    if (this._compositionResult === this._control!.value) { this._compositionResult = undefined; return; }
    this._compositionResult = undefined; this._edit();
  };
  private _onNativeChange = (event: Event): void => { event.stopPropagation(); };
  private _onCompositionStart = (): void => { if (!this.disabled && !this.readonly) { this._composing = true; this._compositionResult = undefined; this._cancel(); this._active = ''; this._results = []; this._status = 'idle'; this._renderResults(); } };
  private _onCompositionEnd = (): void => {
    if (!this._composing) return; this._composing = false;
    if (!this.disabled && !this.readonly) { this._compositionResult = this._control!.value; this._edit(); }
  };
  private _onFocusOut = (event: Event): void => {
    const next = (event as FocusEvent).relatedTarget;
    // Focusout can reach a microtask checkpoint before the browser finishes
    // focusing a popup action. Its same-root destination is authoritative.
    if (next instanceof Node && this.shadowRoot!.contains(next)) return;
    queueMicrotask(() => {
      if (!this.isConnected || this.shadowRoot!.activeElement) return;
      this._commit(); this._close('blur'); this._onBlur();
    });
  };
  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.isComposing || this._composing || this.disabled || this.readonly || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); const direction = event.key === 'ArrowDown' ? 1 : -1;
      if (!this.open) { if (this._present('keyboard')) this._request(true, direction); return; }
      if (this.status === 'loading') { this._direction = direction; return; }
      const enabled = this._results.filter(option => !option.disabled); if (!enabled.length) return;
      const current = enabled.findIndex(option => option.value === this._active);
      const next = current < 0 ? direction > 0 ? 0 : enabled.length - 1 : Math.max(0, Math.min(enabled.length - 1, current + direction));
      this._active = enabled[next].value; this._updatePopup();
      this._list!.querySelector<HTMLElement>('[aria-selected=true]')?.scrollIntoView?.({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      if (this.open && this._active) { event.preventDefault(); this._choose(this._active); }
      else this._commit();
    } else if (event.key === 'Tab') { this._commit(); this._close('tab'); }
  };
  private _onPopupDown = (event: MouseEvent): void => { if (event.target instanceof HTMLElement && event.target.closest('[data-choice]')) event.preventDefault(); };
  private _onPopupClick = (event: Event): void => {
    const button = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('[data-choice]') : null;
    if (button) this._choose(button.dataset.choice!);
  };
  private _onToggle = (): void => { if (this.open) { this._close('toggle'); this.focus(); } else this.show(); };
  private _onClear = (): void => { this.clear(); };
  private _onRetry = (): void => { this.retry(); };
  protected _bind(): void {
    this._control!.addEventListener('input', this._onInput); this._control!.addEventListener('change', this._onNativeChange);
    (this._control as HTMLInputElement).addEventListener('keydown', this._onKeyDown); this._control!.addEventListener('compositionstart', this._onCompositionStart);
    this._control!.addEventListener('compositionend', this._onCompositionEnd); this.shadowRoot!.addEventListener('focusout', this._onFocusOut);
    this._list!.addEventListener('mousedown', this._onPopupDown); this._list!.addEventListener('click', this._onPopupClick);
    this._toggle!.addEventListener('click', this._onToggle); this._clear!.addEventListener('click', this._onClear); this._retry!.addEventListener('click', this._onRetry);
  }
  protected _unbind(): void {
    this._close('disconnect'); this._composing = false; this._compositionResult = undefined;
    this._control?.removeEventListener('input', this._onInput); this._control?.removeEventListener('change', this._onNativeChange);
    (this._control as HTMLInputElement | undefined)?.removeEventListener('keydown', this._onKeyDown); this._control?.removeEventListener('compositionstart', this._onCompositionStart);
    this._control?.removeEventListener('compositionend', this._onCompositionEnd); this.shadowRoot!.removeEventListener('focusout', this._onFocusOut);
    this._list?.removeEventListener('mousedown', this._onPopupDown); this._list?.removeEventListener('click', this._onPopupClick);
    this._toggle?.removeEventListener('click', this._onToggle); this._clear?.removeEventListener('click', this._onClear); this._retry?.removeEventListener('click', this._onRetry);
  }
  protected _formValue(): string { return this.value; }
  protected _formState(): string { return JSON.stringify({ value: this.value, query: this.query, option: this.selectedOption, custom: this._custom, editing: this._editing }); }
  protected _reset(): void { this._close('reset'); this._assign(this.defaultValue); this._committed = this._signature(); }
  protected _restore(state: string): void {
    try {
      const saved = JSON.parse(state);
      if (!saved || typeof saved.value !== 'string' || typeof saved.query !== 'string' || typeof saved.custom !== 'boolean' || typeof saved.editing !== 'boolean') return;
      this._close('restore'); this._value = saved.value; this._query = saved.query; this._custom = saved.custom; this._editing = saved.editing;
      this._selected = normalizeChoiceOptions([saved.option]).find(option => option.value === saved.value) || null; this._committed = this._signature();
    } catch { /* Ignore malformed browser state without changing the current value. */ }
  }
  protected _constraints(): { flags: Partial<FieldValidity>; message: string } {
    if ((this._custom && !this.allowCustom) || this._selected?.disabled) return { flags: { badInput: true }, message: 'Choose an available suggestion.' };
    if (this.required && !this.value) return { flags: { valueMissing: true }, message: 'Choose a value.' };
    return { flags: {}, message: '' };
  }
}
if (!customElements.get('qds-combobox')) customElements.define('qds-combobox', QdsCombobox);
